import { useState, type PropsWithChildren } from 'react'

import { useTranslation } from 'react-i18next'

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  obterFluxoPagamentoLabel,
  type IFluxoPagamento,
} from '@/features/simulador/shared/http/get-fluxo-pagamentos-simulacao.service'
import { cn } from '@/infra/tailwind'

export interface IFluxoPagamentoProps {
  dados?: IFluxoPagamento[]
  calcularDados?: () => IFluxoPagamento[]
  titulo: string
  containerClassName?: string
}

export function FluxoPagamentoSheet({
  dados,
  calcularDados,
  titulo,
  children,
  containerClassName,
}: PropsWithChildren<IFluxoPagamentoProps>) {
  const { t } = useTranslation('simulador')
  const [dadosCalculados, setDadosCalculados] = useState<IFluxoPagamento[]>([])
  const fluxoPagamentoMap = obterFluxoPagamentoLabel(t)
  const handleOpenChange = (open: boolean) => {
    if (open && calcularDados) {
      setDadosCalculados(calcularDados())
    }
  }

  const dadosParaRenderizar = dados ?? dadosCalculados

  return (
    <Sheet onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className={cn('w-full bg-white', containerClassName)} aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle variant="info">{titulo}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100svh-120px)]" aria-describedby={undefined}>
          <div className="mt-2 space-y-6 pr-6">
            {dadosParaRenderizar?.map((anoData) => (
              <div key={anoData.ano} className="space-y-4">
                <h3 className="text-base font-semibold text-neutral-600">{anoData.ano}</h3>
                <div className="space-y-4">
                  {anoData.parcelas.map((parcela, index) => (
                    <div key={`${anoData.ano}-${index}`}>
                      <div className="font-nunito flex items-start justify-between gap-9 text-sm">
                        <div className="flex items-start gap-3">
                          <span className="text-sm font-semibold text-neutral-700">{parcela.data}</span>
                          <span className="text-sm text-neutral-600">{fluxoPagamentoMap[parcela.tipoDaParcela]}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm whitespace-nowrap text-neutral-700">{parcela.valor}</span>
                          {parcela.valorConvertido ? (
                            <>
                              <hr className="h-4 w-px bg-neutral-300" />
                              <span className="w-28 text-left text-sm whitespace-nowrap text-neutral-700">
                                {parcela.valorConvertido}
                              </span>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
